import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { api } from '../../lib/api';
import { getEditorLocale } from './editor-locale';

interface Props {
  value: string;
  onChange: (v: string) => void;
  blockType: string;
  fieldName: string;
}

// Campo de texto de Puck con botón ✨ que genera copy del bloque vía /api/ai/block,
// en el locale de la página actual. Estilado en línea (Puck rinde fuera de Tailwind
// del panel en algunos contextos), siguiendo los tokens CSS var del editor.
export default function AiTextInput({ value, onChange, blockType, fieldName }: Props) {
  const [loading, setLoading] = useState(false);
  const [glow, setGlow] = useState(false);

  async function generate() {
    setLoading(true);
    try {
      const { value: generated } = await api.ai.block(blockType, fieldName, {
        context: value || undefined,
        locale: getEditorLocale(),
      });
      if (generated) {
        onChange(generated);
        setGlow(true);
        window.setTimeout(() => setGlow(false), 1600);
      }
    } catch {
      /* sin API key / límite: se deja el valor actual */
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
      <div style={{ position: 'relative', flex: 1, borderRadius: 6 }}>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '100%', background: 'rgb(var(--ui-bg-ch))', border: '1px solid rgb(var(--ui-border-ch))',
            borderRadius: 6, padding: '8px 10px', fontSize: 13, color: 'rgb(var(--ui-text-ch))',
          }}
        />
        <AnimatePresence>
          {glow && (
            <motion.div
              key="glow"
              aria-hidden
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              style={{
                position: 'absolute', inset: 0, borderRadius: 6, padding: 2, pointerEvents: 'none',
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor', maskComposite: 'exclude',
              }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1.4, ease: 'linear', repeat: Infinity }}
                style={{
                  position: 'absolute', inset: '-50%',
                  background: 'conic-gradient(from 0deg, #3b82f6, #a855f7, #ec4899, #f59e0b, #22d3ee, #3b82f6)',
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <motion.button
        type="button"
        onClick={generate}
        disabled={loading}
        whileTap={{ scale: 0.9 }}
        animate={loading ? { scale: [1, 1.12, 1] } : { scale: 1 }}
        transition={loading ? { repeat: Infinity, duration: 0.9 } : { duration: 0.15 }}
        title="Generar con IA"
        style={{
          display: 'flex', alignItems: 'center', gap: 4, padding: '0 10px',
          background: 'transparent', border: '1px solid rgb(var(--ui-accent-ch))', borderRadius: 6,
          color: 'rgb(var(--ui-accent-ch))', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        <Sparkles size={14} />
      </motion.button>
    </div>
  );
}
